import { useState } from "react";
import { Clock, Globe, HeartHandshake, Send, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ENQUIRY_PRODUCTS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const STATS = [
  { icon: Clock, value: "17+", label: "Years of Expertise" },
  { icon: Globe, value: "Pan-India", label: "Service Coverage" },
  { icon: ShieldCheck, value: "End-to-End", label: "Lifecycle Support" },
  { icon: HeartHandshake, value: "24/7", label: "Technical Assistance" },
];

export const EnquiryFields = ({ form, setForm, testPrefix = "input" }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full rounded-md border border-medis-silver/40 bg-white text-medis-navy text-[14px] py-3 focus:border-medis-teal focus:outline-none px-4";
  return (
    <>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Full Name *</label>
          <input placeholder="Dr. Anjali Rao" className={inputCls} data-testid={`${testPrefix}-fullname`} type="text" value={form.name} onChange={set("name")} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Hospital / Diagnostic Centre</label>
          <input placeholder="Sterling Hospital" className={inputCls} data-testid={`${testPrefix}-facility`} type="text" value={form.facility} onChange={set("facility")} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Phone Number *</label>
          <input placeholder="+91 90000 00000" className={inputCls} data-testid={`${testPrefix}-phone`} type="tel" value={form.phone} onChange={set("phone")} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Email Address *</label>
          <input placeholder="you@hospital.com" className={inputCls} data-testid={`${testPrefix}-email`} type="email" value={form.email} onChange={set("email")} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">City</label>
          <input placeholder="Pune" className={inputCls} data-testid={`${testPrefix}-city`} type="text" value={form.city} onChange={set("city")} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Interested Product / Solution *</label>
          <select className="rounded-md border border-medis-silver/40 bg-white text-medis-navy text-[14px] px-4 py-3 focus:border-medis-teal focus:outline-none" data-testid={`${testPrefix}-product`} value={form.product} onChange={set("product")}>
            <option value="">Select one…</option>
            {ENQUIRY_PRODUCTS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-1.5">
        <label className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-medis-gray">Message *</label>
        <textarea
          rows="4"
          placeholder="Share your requirement, timeline, or any specific questions."
          className="rounded-md border border-medis-silver/40 bg-white text-medis-navy text-[14px] px-4 py-3 focus:border-medis-teal focus:outline-none resize-none"
          data-testid={`${testPrefix}-message`}
          value={form.message}
          onChange={set("message")}
        />
      </div>
    </>
  );
};

export const emptyForm = { name: "", facility: "", phone: "", email: "", city: "", product: "", message: "" };

export const validateForm = (form) => {
  if (!form.name.trim()) return "Please enter your full name.";
  if (!form.phone.trim()) return "Please enter your phone number.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email address.";
  if (!form.product) return "Please select a product or solution.";
  if (!form.message.trim()) return "Please enter your message.";
  return null;
};

export const submitEnquiry = async (form, endpoint = "/enquiries") => {
  return api.post(endpoint, form);
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSending(true);
    try {
      const res = await submitEnquiry(form);
      toast.success(res.data.message || "Enquiry received. Our team will contact you shortly.");
      setForm(emptyForm);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to submit enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="pt-8 pb-12 lg:pt-10 lg:pb-16 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="relative overflow-hidden rounded-2xl bg-medis-blue text-white">
          <div className="absolute inset-0 bg-grid-dark opacity-30" />
          <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-white/5 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/4 h-64 w-64 rounded-full bg-medis-teal/15 blur-3xl pointer-events-none" />
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-medis-teal/20 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 items-stretch">
            <div className="flex flex-col justify-center p-10 sm:p-12 lg:p-14">
              <Reveal>
                <div className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-medis-teal" /> Ready to Upgrade?
                </div>
                <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-[30px] leading-[1.1] tracking-tight">
                  Ready to Upgrade Your Imaging Infrastructure?
                </h2>
                <p className="mt-3 text-white/65 text-[15px] leading-relaxed max-w-sm">
                  Talk to MEDIS about MRI, CT, Cath Lab, RF shielding, helium and technical support tailored to your
                  facility.
                </p>
              </Reveal>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {STATS.map((s) => (
                  <div key={s.label} className="rounded-xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition-colors">
                    <div className="h-8 w-8 rounded-lg bg-medis-teal/20 flex items-center justify-center mb-3">
                      <s.icon className="h-4 w-4 text-medis-teal" />
                    </div>
                    <div className="text-[22px] font-bold text-white leading-tight tracking-tight">{s.value}</div>
                    <div className="text-[11.5px] text-white/50 font-medium mt-1 uppercase tracking-[0.1em]">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center p-6 sm:p-8">
              <form className="w-full rounded-xl bg-white p-6 sm:p-8 shadow-2xl" data-testid="enquiry-form" onSubmit={onSubmit}>
                <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">
                  <User className="h-3.5 w-3.5" /> Enquiry Form
                </div>
                <h3 className="mt-3 font-display font-bold text-medis-navy text-2xl sm:text-[28px] leading-tight tracking-tight">
                  Tell us about your requirement.
                </h3>
                <EnquiryFields form={form} setForm={setForm} testPrefix="input" />
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <p className="text-[12px] text-medis-gray leading-snug max-w-xs">
                    By submitting, you agree to be contacted by MEDIS Group LLP. We respect your data.
                  </p>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 bg-medis-blue text-white px-6 py-3.5 rounded-md text-[14px] font-semibold hover:bg-medis-blue-dark transition-colors disabled:opacity-70"
                    disabled={sending}
                    data-testid="submit-enquiry"
                  >
                    {sending ? "Submitting…" : "Submit Enquiry"}
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
